import { Button } from "@chakra-ui/react";
import { useAllocationRuleTableListSuspense } from "gen/endpoints/allocation-rule-table/allocation-rule-table";
import { AllocationRuleListParams } from "gen/model/allocationRuleListParams";
import { AllocationRuleStateMachineFieldsEnum } from "gen/model/allocationRuleStateMachineFieldsEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetLinkInfoForRowFn } from "shared/components/table/data/DataTableTypes";
import { rootRoute } from "shared/navigation/Routes";

export interface AllocationsTableProps {
  fundFamilyId: string;
  params?: AllocationRuleListParams;
}

export const AllocationsTable: React.FC<AllocationsTableProps> = ({
  fundFamilyId,
  params,
}) => {
  const { t } = useTranslation(["allocation", "common"]);
  const navigate = useNavigate();

  const { data: tableData } = useAllocationRuleTableListSuspense(
    fundFamilyId,
    params,
  );

  const entityId = params?.entity_id;

  const getLinkInfoForRow = useCallback<GetLinkInfoForRowFn>(
    ({ row }) => {
      const baseRoute = entityId
        ? rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .setup({})
            .entities({})
            .entity({ entityId })
            .allocations({})
        : rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .setup({})
            .entities({})
            .allocations({});
      const cell = row.data?.cells["allocation_state"];
      const metadata = row.data?.metadata;
      return metadata?.type === "id_row" &&
        cell?.metadata?.type === "allocation_rule"
        ? {
            type: "generic",
            to:
              cell.metadata.state === AllocationRuleStateMachineFieldsEnum.draft
                ? baseRoute.setup({ allocationRuleId: metadata.id }).$
                : baseRoute.edit({ allocationRuleId: metadata.id }).$,
          }
        : undefined;
    },
    [fundFamilyId, entityId],
  );

  const addButton = (
    <Button
      size="xs"
      onClick={() => {
        const entitiesRoute = rootRoute({})
          .fundFamilies({})
          .fundFamily({ fundFamilyId })
          .setup({})
          .entities({});
        navigate(
          params?.entity_id
            ? entitiesRoute
                .entity({ entityId: params.entity_id })
                .allocations({})
                .create({}).$
            : entitiesRoute.allocations({}).create({}).$,
        );
      }}
    >
      {t("allocation:addRule")}
    </Button>
  );

  return (
    <TableBox>
      <TableBoxHeader>
        <TableBoxTitle>{t("common:allocationRules")}</TableBoxTitle>
        <Restricted permission={PermissionTypeEnum.service}>
          <DataTableBoxActions createAction={addButton} />
        </Restricted>
      </TableBoxHeader>
      <TableBoxBody>
        <DataTable
          data={tableData}
          getLinkInfoForRow={getLinkInfoForRow}
          emptyStateProps={{
            title: t("allocation:empty.title"),
            description: t("allocation:empty.description"),
          }}
        />
      </TableBoxBody>
    </TableBox>
  );
};
